import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types'
import { LIST_TEMPLATE, ListTemplateContext } from '@contexts/list-template'
import { ContentType } from '@entries/content-types'
import ContentfulBlogArticle from '@entries/contentful-blog-article-link'
import ContentfulCtaSecondaryRefresh from '@entries/contentful-cta-secondary-refresh'
import ContentfulExternalLink from '@entries/contentful-external-link'
import ContentfulFootnoteSup from '@entries/contentful-footnote-sup'
import ContentfulImage from '@entries/contentful-image'
import ContentfulMultipleNumberHighlight from '@entries/contentful-multiple-number-highlight'
import ContentfulNumberedPunchlineRightLeftTextWithImage from '@entries/contentful-numbered-punchline-right-left-text-with-image'
import ContentfulPDFLink from '@entries/contentful-pdf-link'
import ContentfulStaticPageLink from '@entries/contentful-static-page-link'
import ContentfulYoutubePlayer from '@entries/contentful-youtube-player'
import { withDisplayName } from '@hocs/with-display-name'
import { bold } from '@render-mark/bold'
import { listItem } from '@render-node/list-item'
import { olList } from '@render-node/ol-list'
import { paragraphHeading3 } from '@render-node/paragraph-heading-3'
import { paragraphSpaced } from '@render-node/paragraph-spaced'
import { ulList } from '@render-node/ul-list'
import { Entry } from '@resolvers/entry'
import RichText from '@resolvers/rich-text'
import clsx from 'clsx/lite'
import { graphql } from 'gatsby'
import React from 'react'

import contentfulDeprecatedPageLink from './contentful-deprecated-page-link'

const ContentfulRightLeftTextWithImage: Utils.EntryFC<
  Queries.ContentfulRightLeftTextWithImage
> = ({ content: { title, content, visual, cta, textPosition, listStyle } }) => (
  <div
    className={clsx(
      'group/content-type group/theme is-dark visual-alternation-block flex flex-col-reverse items-center gap-6 laptop:gap-32',
      textPosition === 'Droite' ? 'tablet:flex-row' : 'tablet:flex-row-reverse'
    )}
  >
    <div className="flex flex-1 flex-col gap-2 pb-10 pt-4 tablet:pb-0 tablet:pt-0">
      <RichText
        content={title}
        options={{
          renderNode: {
            [BLOCKS.PARAGRAPH]: paragraphHeading3,
            [INLINES.EMBEDDED_ENTRY]: {
              [ContentType.ContentfulFootnote]: ContentfulFootnoteSup,
            },
          },
          renderMark: {
            [MARKS.BOLD]: (text) => (
              <span className="text-theme-primary-main">{text}</span>
            ),
          },
        }}
      />
      <ListTemplateContext.Provider
        value={(listStyle as LIST_TEMPLATE) || LIST_TEMPLATE.DISC}
      >
        <RichText
          content={content}
          options={{
            renderMark: {
              [MARKS.BOLD]: bold,
            },
            renderNode: {
              [BLOCKS.PARAGRAPH]: paragraphSpaced,
              [BLOCKS.OL_LIST]: olList,
              [BLOCKS.UL_LIST]: ulList,
              [BLOCKS.LIST_ITEM]: listItem,
              [BLOCKS.EMBEDDED_ENTRY]: {
                [ContentType.ContentfulNumberedPunchline]:
                  ContentfulNumberedPunchlineRightLeftTextWithImage,
              },
              [INLINES.EMBEDDED_ENTRY]: {
                [ContentType.ContentfulFootnote]: ContentfulFootnoteSup,
              },
              [INLINES.ENTRY_HYPERLINK]: {
                [ContentType.ContentfulExternalLink]: ContentfulExternalLink,
                [ContentType.ContentfulStaticPage]: ContentfulStaticPageLink,
                [ContentType.ContentfulPdfLink]: ContentfulPDFLink,
                [ContentType.ContentfulBlogArticle]: ContentfulBlogArticle,
                [ContentType.ContentfulDeprecatedPage]:
                  contentfulDeprecatedPageLink,
              },
            },
          }}
        />
      </ListTemplateContext.Provider>
      <Entry
        content={cta}
        mapping={{
          [ContentType.ContentfulCta]: ContentfulCtaSecondaryRefresh,
        }}
      />
    </div>
    <div className="w-full flex-1">
      <Entry
        content={visual}
        mapping={{
          [ContentType.ContentfulMultipleNumberHighlight]:
            ContentfulMultipleNumberHighlight,
          [ContentType.ContentfulImage]: ContentfulImage,
          [ContentType.ContentfulYoutubePlayer]: ContentfulYoutubePlayer,
        }}
      />
    </div>
  </div>
)

export const query = graphql`
  fragment ContentfulRightLeftTextWithImage on ContentfulRightLeftTextWithImage {
    ...ContentfulEntry
    title {
      rich
      references {
        ...ContentfulFootnoteSup
      }
    }
    content {
      rich
      references {
        ...ContentfulFootnoteSup
        ...ContentfulExternalLink
        ...ContentfulStaticPageLink
        ...ContentfulPDFLink
        ...ContentfulBlogArticleLink
        ...ContentfulDeprecatedPageLink
        ...ContentfulNumberedPunchlineRightLeftTextWithImage
      }
    }
    visual {
      ...ContentfulNumbersHighlight
      ...ContentfulImage
      ...ContentfulYoutubePlayer
    }
    cta {
      ...ContentfulCtaSecondaryRefresh
    }
    textPosition
    listStyle
  }
`

export default withDisplayName(
  ContentfulRightLeftTextWithImage,
  'ContentfulRightLeftTextWithImage'
)
